/**
 * /app/$tenantSlug/wfh — Work-from-home requests (migration 063).
 *
 * Leave-shaped but BALANCE-FREE: a WFH request never debits a leave
 * balance; an approved range overlays attendance (a WFH day reads 'wfh'
 * not 'absent') server-side. v1 always requires manager approval.
 *
 * This page is the employee surface: submit a WFH range + see your own
 * requests. Managers/HR decide from the WFH approvals route.
 */

import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { useLeaveRoles } from "@/components/leave/use-leave-roles";
import { LeaveFeatureGate } from "@/components/leave/leave-feature-gate";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  useWFHRequests,
  useCreateWFHRequest,
  useCancelWFHRequest,
} from "@/hooks/use-wfh";
import { toApiError } from "@/lib/api";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/wfh/")({
  component: WFHRoute,
});

function WFHRoute() {
  const { tenantSlug } = Route.useParams();
  const { isApprover } = useLeaveRoles();
  const mine = useWFHRequests(undefined, "self");
  const create = useCreateWFHRequest();
  const cancel = useCancelWFHRequest();

  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [reason, setReason] = useState("");

  async function submit() {
    if (!start || !end) {
      toast.error("Start and end dates are required");
      return;
    }
    try {
      await create.mutateAsync({
        start_date: start,
        end_date: end,
        reason: reason.trim() || undefined,
      });
      toast.success("WFH request submitted");
      setStart("");
      setEnd("");
      setReason("");
    } catch (e) {
      const err = toApiError(e);
      toast.error(
        err.error === "no_working_days"
          ? "The selected range has no working days"
          : (err.message ?? "Failed to submit"),
      );
    }
  }

  return (
    <TimeAndLeaveShell
      title="Work from home"
      description="Plan WFH days. No leave balance is used — your manager approves and your attendance reflects WFH automatically."
      actions={
        isApprover ? (
          <Button asChild variant="outline">
            <Link to="/app/$tenantSlug/wfh/approvals" params={{ tenantSlug }}>
              Approvals
            </Link>
          </Button>
        ) : undefined
      }
    >
      <LeaveFeatureGate>
        <div className="space-y-6">
          <Card className="max-w-2xl">
            <CardHeader>
              <CardTitle>New WFH request</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="s">Start date</Label>
                  <Input
                    id="s"
                    type="date"
                    value={start}
                    onChange={(e) => setStart(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="e">End date</Label>
                  <Input
                    id="e"
                    type="date"
                    value={end}
                    onChange={(e) => setEnd(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="r">Reason (optional)</Label>
                <Textarea
                  id="r"
                  value={reason}
                  maxLength={2000}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Why are you working from home?"
                />
              </div>
              <p className="text-muted-foreground text-xs">
                No leave balance impact. Weekends and holidays are excluded
                from the day count.
              </p>
              <Button onClick={submit} disabled={create.isPending}>
                {create.isPending ? "Submitting…" : "Submit WFH request"}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>My WFH requests</CardTitle>
            </CardHeader>
            <CardContent>
              {mine.data && mine.data.wfh_requests.length > 0 ? (
                <ul className="divide-y text-sm">
                  {mine.data.wfh_requests.map((w) => (
                    <li
                      key={w.id}
                      className="flex items-center justify-between py-2"
                    >
                      <Link
                        to="/app/$tenantSlug/wfh/$id"
                        params={{ tenantSlug, id: String(w.id) }}
                        className="hover:underline"
                      >
                        {w.start_date} → {w.end_date} ({w.total_days} day
                        {w.total_days === 1 ? "" : "s"})
                      </Link>
                      <span className="flex items-center gap-2">
                        <Badge variant="outline">{w.status}</Badge>
                        {(w.status === "pending" ||
                          w.status === "approved") && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => cancel.mutate(w.id)}
                          >
                            Cancel
                          </Button>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground text-sm">
                  No WFH requests yet.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </LeaveFeatureGate>
    </TimeAndLeaveShell>
  );
}
